import axios from '../axiosConfig';
import logger from '../utils/logger';

class ReferenceService {
  constructor() {
    this.cache = {
      countries: { data: null, timestamp: null },
      timezones: { data: null, timestamp: null },
      locationData: { data: null, timestamp: null },
      languages: { data: null, timestamp: null }
    };
    this.cacheTimeout = 30 * 60 * 1000; // 30 minutes
    // Keep reference requests relative when VITE_API_URL is omitted so the
    // configured Axios instance, dev proxy, or deployment origin controls the
    // API target. Falling back to a frontend dev port causes avoidable CORS
    // failures when Vite moves between ports.
    this.baseURL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
  }

  // Check if cache is valid
  isCacheValid(cacheKey) {
    const cache = this.cache[cacheKey];
    if (!cache.data || !cache.timestamp) {
      return false;
    }
    return Date.now() - cache.timestamp < this.cacheTimeout;
  }

  // Get countries with caching
  async getCountries() {
    if (this.isCacheValid('countries')) {
      logger.debug('Using cached countries data');
      return this.cache.countries.data;
    }

    try {
      logger.debug('Fetching countries from API');
      const response = await axios.get(`${this.baseURL}/api/reference/countries`);
      
      if (response.data.success) {
        this.cache.countries = {
          data: response.data.data,
          timestamp: Date.now()
        };
        
        logger.debug('Countries fetched successfully', { count: response.data.data.length });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch countries');
      }
    } catch (error) {
      logger.warn('Error fetching countries', error);
      
      // Return cached data if available, otherwise fallback
      if (this.cache.countries.data) {
        logger.debug('Using stale cached countries data due to API error');
        return this.cache.countries.data;
      }
      
      // Minimal fallback
      return [{ iso_code: 'US', name: 'United States', locale_code: 'en-US' }];
    }
  }

  // Get timezones with caching
  async getTimezones() {
    if (this.isCacheValid('timezones')) {
      logger.debug('Using cached timezones data');
      return this.cache.timezones.data;
    }

    try {
      logger.debug('Fetching timezones from API');
      const response = await axios.get(`${this.baseURL}/api/reference/timezones`);
      
      if (response.data.success) {
        this.cache.timezones = {
          data: response.data.data,
          timestamp: Date.now()
        };
        
        logger.debug('Timezones fetched successfully', { count: response.data.data.length });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch timezones');
      }
    } catch (error) {
      logger.warn('Error fetching timezones', error);
      
      // Return cached data if available, otherwise fallback
      if (this.cache.timezones.data) {
        logger.debug('Using stale cached timezones data due to API error');
        return this.cache.timezones.data;
      }
      
      // Minimal fallback
      return [{ iana_name: 'UTC', display_name: 'UTC', utc_offset: '+00:00' }];
    }
  }

  // Get both countries and timezones in one call
  async getLocationData({ allowStaticFallback = true } = {}) {
    if (this.isCacheValid('locationData')) {
      logger.debug('Using cached location data');
      return this.cache.locationData.data;
    }

    try {
      logger.debug('Fetching location data from API');
      const response = await axios.get(`${this.baseURL}/api/reference/location-data`);
      
      if (response.data.success) {
        this.cache.locationData = {
          data: response.data.data,
          timestamp: Date.now()
        };
        
        // Also cache individual arrays
        this.cache.countries = {
          data: response.data.data.countries,
          timestamp: Date.now()
        };
        this.cache.timezones = {
          data: response.data.data.timezones,
          timestamp: Date.now()
        };

        logger.debug('Location data fetched successfully', {
          countries: response.data.data.countries.length,
          timezones: response.data.data.timezones.length
        });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch location data');
      }
    } catch (error) {
      logger.warn('Error fetching location data', error);
      
      // Return cached data if available, otherwise fallback
      if (this.cache.locationData.data) {
        logger.debug('Using stale cached location data due to API error');
        return this.cache.locationData.data;
      }

      // Entity creation must use a value the server actually published from
      // its reference tables. A synthesized UTC option would look legitimate
      // and could bypass the required-selection invariant.
      if (!allowStaticFallback) {
        throw error;
      }
      
      // Minimal fallback
      return {
        countries: [{ iso_code: 'US', name: 'United States', locale_code: 'en-US' }],
        timezones: [{ iana_name: 'UTC', display_name: 'UTC', utc_offset: '+00:00' }]
      };
    }
  }

  // Get specific country by ISO code
  async getCountry(isoCode) {
    try {
      logger.debug('Fetching specific country', { isoCode });
      const response = await axios.get(`${this.baseURL}/api/reference/countries/${isoCode}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Country not found');
      }
    } catch (error) {
      logger.warn('Error fetching country', error);
      
      // Fallback to countries list
      const countries = await this.getCountries();
      return countries.find(c => c.iso_code === isoCode) || null;
    }
  }

  // Get specific timezone by IANA name
  async getTimezone(ianaName) {
    try {
      logger.debug('Fetching specific timezone', { ianaName });
      const response = await axios.get(`${this.baseURL}/api/reference/timezones/${ianaName}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Timezone not found');
      }
    } catch (error) {
      logger.warn('Error fetching timezone', error);
      
      // Fallback to timezones list
      const timezones = await this.getTimezones();
      return timezones.find(t => t.iana_name === ianaName) || null;
    }
  }

  // Get languages with locale mapping
  async getLanguages() {
    if (this.isCacheValid('languages')) {
      logger.debug('Using cached languages data');
      return this.cache.languages.data;
    }

    try {
      logger.debug('Fetching languages from API');
      const response = await axios.get(`${this.baseURL}/api/reference/languages`);
      
      if (response.data.success) {
        this.cache.languages = {
          data: response.data.data,
          timestamp: Date.now()
        };
        
        logger.debug('Languages fetched successfully', { count: response.data.data.length });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch languages');
      }
    } catch (error) {
      logger.warn('Error fetching languages', error);
      
      // Return cached data if available, otherwise minimal fallback
      if (this.cache.languages.data) {
        logger.debug('Using stale cached languages data due to API error');
        return this.cache.languages.data;
      }
      
      // Languages are controlled by the database, including is_active. Without
      // a fresh or cached API result, do not invent selectable language options.
      return [];
    }
  }

  // Get locale for a specific language code
  async getLocaleForLanguage(languageCode) {
    try {
      logger.debug('Fetching locale for language', { languageCode });
      const response = await axios.get(`${this.baseURL}/api/reference/languages/${languageCode}/locale`);
      
      if (response.data.success && response.data.data?.locale_code) {
        return response.data.data.locale_code;
      } else {
        throw new Error(response.data.error || 'Failed to fetch locale');
      }
    } catch (error) {
      logger.warn('Error fetching locale for language', error);
      
      // Fallback to languages list
      const languages = await this.getLanguages();
      const language = languages.find(l => l.language_code === languageCode);
      
      if (language) {
        return language.locale_code;
      }
      
      // Default to English if language not found
      return 'en-US';
    }
  }

  // Clear cache (useful for testing or forced refresh)
  clearCache() {
    this.cache = {
      countries: { data: null, timestamp: null },
      timezones: { data: null, timestamp: null },
      locationData: { data: null, timestamp: null },
      languages: { data: null, timestamp: null }
    };
    logger.debug('Reference service cache cleared');
  }

}

// Export singleton instance
export default new ReferenceService();
